import { type JSX, type ReactNode, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TuneIcon from '@mui/icons-material/Tune';
import CampaignIcon from '@mui/icons-material/Campaign';
import DevicesIcon from '@mui/icons-material/Devices';
import TimelineIcon from '@mui/icons-material/Timeline';
import HistoryIcon from '@mui/icons-material/History';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LaunchIcon from '@mui/icons-material/Launch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import ShieldIcon from '@mui/icons-material/Shield';
import KeyIcon from '@mui/icons-material/VpnKey';

import {
  cloneTenant,
  deleteFleet,
  fetchLicense,
  type FetchResult,
  type MeTenant,
  signup,
} from '../api.js';
import { ConfigEditor, type EditorTab } from '../components/ConfigEditor.js';
import { confirmDialog, promptDialog } from '../components/dialogs.js';
import { LicenseDetails } from '../components/LicenseDetails.js';
import { LicenseLookup } from '../components/LicenseLookup.js';
import { TwoFactorSetup } from '../components/TwoFactorSetup.js';
import { type FleetSection, fleetHref, goFleet, replaceFleet, useFleetRoute } from './route.js';
import { OverviewSection } from './OverviewSection.js';
import { BroadcastsSection } from './sections/BroadcastsSection.js';
import { DevicesSection } from './sections/DevicesSection.js';
import { EventsSection } from './sections/EventsSection.js';
import { SubscriptionSection } from './sections/SubscriptionSection.js';

const DRAWER_WIDTH = 320;

const SECTION_META: { id: FleetSection; label: string; icon: ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <DashboardIcon /> },
  { id: 'launcher', label: 'Launcher', icon: <TuneIcon /> },
  { id: 'broadcasts', label: 'Broadcasts', icon: <CampaignIcon /> },
  { id: 'devices', label: 'Devices', icon: <DevicesIcon /> },
  { id: 'telemetry', label: 'Telemetry', icon: <TimelineIcon /> },
  { id: 'audit', label: 'Audit', icon: <HistoryIcon /> },
];
/** Map a dashboard section to the embedded ConfigEditor tab. */
const TAB_FOR: Record<Exclude<FleetSection, 'overview'>, EditorTab> = {
  launcher: 'launcher',
  broadcasts: 'broadcasts',
  devices: 'devices',
  telemetry: 'telemetry',
  audit: 'audit',
  subscription: 'upgrade',
};

/**
 * The signed-in experience: a full-viewport MUI dashboard. The top-left fleet
 * switcher picks the active tenant; the sidebar routes between per-fleet
 * sections (`#fleet/<id>/<section>`); the account menu (top-right) holds
 * two-factor, license lookup, and sign-out. The four old row-actions live here
 * now — Open/Duplicate/Delete in the fleet menu, "Customize" *is* the Launcher
 * section.
 */
export function FleetDashboard({
  email,
  tenants,
  totpEnabled,
  onSignOut,
  onRefresh,
}: {
  email: string;
  tenants: MeTenant[];
  totpEnabled: boolean;
  onSignOut: () => void;
  onRefresh: () => void | Promise<unknown>;
}): JSX.Element {
  const route = useFleetRoute();
  const active = tenants.find((t) => t.tenantId === route.tenantId) ?? tenants[0]!;
  const section = route.section;

  // Land on a real fleet route if the hash doesn't already name a known one
  // (e.g. arriving from the sign-in `#fleets` default or a deleted tenant).
  useEffect(() => {
    if (route.tenantId === null || !tenants.some((t) => t.tenantId === route.tenantId)) {
      replaceFleet(active.tenantId, route.section);
      // replaceState doesn't fire hashchange; nudge the router.
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.tenantId, tenants]);

  const [accountEl, setAccountEl] = useState<null | HTMLElement>(null);
  const [fleetMenuEl, setFleetMenuEl] = useState<null | HTMLElement>(null);
  const [dialog, setDialog] = useState<null | '2fa' | 'license' | 'new'>(null);
  const [busy, setBusy] = useState(false);

  async function duplicate(): Promise<void> {
    setFleetMenuEl(null);
    if (active.editToken === null) return;
    const name = await promptDialog({
      title: 'Duplicate fleet',
      message: `Name for the copy of "${active.deploymentName}":`,
      defaultValue: `${active.deploymentName} copy`,
    });
    if (name === null) return;
    setBusy(true);
    const res = await cloneTenant({ sourceTenantId: active.tenantId, editToken: active.editToken, deploymentName: name.trim() });
    setBusy(false);
    if (res.kind === 'ok') {
      await onRefresh();
      goFleet(res.tenantId, 'launcher');
    }
  }

  async function remove(): Promise<void> {
    setFleetMenuEl(null);
    if (active.editToken === null) return;
    const ok = await confirmDialog({
      title: 'Delete fleet',
      message: `Permanently delete "${active.deploymentName}"? This removes its config, licenses, devices, broadcasts, and telemetry. This cannot be undone.`,
      destructive: true,
      confirmLabel: 'Delete fleet',
      requireText: active.deploymentName,
    });
    if (!ok) return;
    setBusy(true);
    const res = await deleteFleet({ tenantId: active.tenantId, editToken: active.editToken });
    setBusy(false);
    if (res.kind === 'ok') {
      const next = tenants.find((t) => t.tenantId !== active.tenantId);
      if (next) replaceFleet(next.tenantId, 'overview');
      await onRefresh();
    }
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <Toolbar variant="dense">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Box component="span" aria-hidden sx={{ color: 'secondary.main', fontSize: 20 }}>▦</Box>
            <Typography variant="subtitle1" fontWeight={700}>MobiLauncher</Typography>
          </Box>
          <Tooltip title={email}>
            <IconButton onClick={(e) => setAccountEl(e.currentTarget)} size="small" data-testid="account-menu-btn">
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 14 }}>
                {email.slice(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={accountEl} open={accountEl !== null} onClose={() => setAccountEl(null)}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 0.5 }}>{email}</Typography>
            <Divider />
            <MenuItem onClick={() => { setAccountEl(null); setDialog('2fa'); }}>
              <ListItemIcon><ShieldIcon fontSize="small" /></ListItemIcon>
              Two-factor authentication
            </MenuItem>
            <MenuItem onClick={() => { setAccountEl(null); setDialog('license'); }}>
              <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>
              Verify a license
            </MenuItem>
            <MenuItem onClick={() => { setAccountEl(null); goFleet(active.tenantId, 'subscription'); }} data-testid="account-subscription">
              <ListItemIcon><CreditCardIcon fontSize="small" /></ListItemIcon>
              Subscription
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAccountEl(null); onSignOut(); }} data-testid="account-signout">
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' } }}
      >
        <Toolbar variant="dense" />
        <Box sx={{ p: 1.5, display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Select
            size="small"
            fullWidth
            value={active.tenantId}
            onChange={(e) => goFleet(e.target.value, section)}
            data-testid="fleet-switcher"
            renderValue={(v) => {
              const t = tenants.find((x) => x.tenantId === v)!;
              return (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography noWrap fontWeight={600}>{t.deploymentName}</Typography>
                  <Chip size="small" label={t.tier} color={t.tier === 'core' ? 'default' : 'secondary'} />
                </Stack>
              );
            }}
          >
            {tenants.map((t) => (
              <MenuItem key={t.tenantId} value={t.tenantId} data-testid={`fleet-option-${t.tenantId}`}>
                {t.deploymentName} · {t.tier}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Fleet actions">
            <IconButton size="small" onClick={(e) => setFleetMenuEl(e.currentTarget)} data-testid="fleet-actions-btn">
              <MoreVertIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={fleetMenuEl} open={fleetMenuEl !== null} onClose={() => setFleetMenuEl(null)}>
            <MenuItem component="a" href={active.launcherUrl} target="_blank" rel="noopener" onClick={() => setFleetMenuEl(null)}>
              <ListItemIcon><LaunchIcon fontSize="small" /></ListItemIcon>
              Open launcher
            </MenuItem>
            <MenuItem onClick={() => void duplicate()} disabled={busy || active.editToken === null} data-testid="fleet-duplicate">
              <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
              Duplicate
            </MenuItem>
            <MenuItem onClick={() => void remove()} disabled={busy || active.editToken === null} data-testid="fleet-delete">
              <ListItemIcon><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
              Delete
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setFleetMenuEl(null); setDialog('new'); }} data-testid="fleet-new">
              <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
              New fleet
            </MenuItem>
          </Menu>
        </Box>
        <Divider />
        <List
          subheader={<ListSubheader component="div" sx={{ bgcolor: 'transparent' }}>Manage</ListSubheader>}
          data-testid="fleet-nav"
        >
          {SECTION_META.map((s) => (
            <ListItemButton
              key={s.id}
              component="a"
              href={fleetHref(active.tenantId, s.id)}
              selected={section === s.id}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                goFleet(active.tenantId, s.id);
              }}
              data-testid={`fleet-nav-${s.id}`}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>{s.icon}</ListItemIcon>
              <ListItemText primary={s.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Toolbar variant="dense" />
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          {section === 'overview' ? (
            <OverviewSection tenant={active} totpEnabled={totpEnabled} onManage2fa={() => setDialog('2fa')} />
          ) : section === 'devices' ? (
            <DevicesSection key={active.tenantId} tenantId={active.tenantId} editToken={active.editToken ?? ''} launcherUrl={active.launcherUrl} />
          ) : section === 'broadcasts' ? (
            <BroadcastsSection key={active.tenantId} tenantId={active.tenantId} editToken={active.editToken ?? ''} />
          ) : section === 'telemetry' ? (
            <EventsSection key={active.tenantId} mode="telemetry" tenantId={active.tenantId} editToken={active.editToken ?? ''} currentTier={active.tier} />
          ) : section === 'audit' ? (
            <EventsSection key={active.tenantId} mode="audit" tenantId={active.tenantId} editToken={active.editToken ?? ''} currentTier={active.tier} />
          ) : section === 'subscription' ? (
            <SubscriptionSection key={active.tenantId} tenantId={active.tenantId} editToken={active.editToken ?? ''} currentTier={active.tier} />
          ) : (
            // Launcher — still the embedded ConfigEditor (BuilderEditor lives here;
            // its MUI rebuild is a later phase).
            <ConfigEditor
              key={active.tenantId}
              embedded
              activeTab={TAB_FOR[section]}
              tenantId={active.tenantId}
              editToken={active.editToken ?? ''}
              launcherUrl={active.launcherUrl}
              onBack={() => goFleet(active.tenantId, 'overview')}
            />
          )}
        </Box>
      </Box>

      <Dialog open={dialog === '2fa'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Two-factor authentication</DialogTitle>
        <DialogContent>
          <TwoFactorSetup enabled={totpEnabled} onChanged={() => void onRefresh()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <VerifyLicenseDialog open={dialog === 'license'} onClose={() => setDialog(null)} />

      <NewFleetDialog
        open={dialog === 'new'}
        email={email}
        onClose={() => setDialog(null)}
        onCreated={async (tenantId) => {
          setDialog(null);
          await onRefresh();
          goFleet(tenantId, 'launcher');
        }}
      />
    </Box>
  );
}

/** Account-menu "Verify a license" — the old lookup tool, now a dialog. */
function VerifyLicenseDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const [result, setResult] = useState<FetchResult | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(id: string): Promise<void> {
    setBusy(true);
    const res = await fetchLicense(id);
    setBusy(false);
    setResult(res);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Verify a license</DialogTitle>
      <DialogContent>
        {result?.kind === 'ok' ? (
          <LicenseDetails license={result.license} rawBlob={result.rawBlob} onLookupAnother={() => setResult(null)} />
        ) : (
          <>
            <LicenseLookup onSubmit={(id) => void submit(id)} busy={busy} />
            {result !== null && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {result.kind === 'malformed' || result.kind === 'network'
                  ? result.message
                  : result.kind === 'revoked'
                    ? 'That license has been revoked.'
                    : 'License not found.'}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Add another fleet under the signed-in account. */
function NewFleetDialog({
  open,
  email,
  onClose,
  onCreated,
}: {
  open: boolean;
  email: string;
  onClose: () => void;
  onCreated: (tenantId: string) => void | Promise<unknown>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create(): Promise<void> {
    if (name.trim().length === 0) return;
    setBusy(true);
    setErr(null);
    const res = await signup({ email, companyName: name.trim() });
    setBusy(false);
    if (res.kind === 'ok') {
      setName('');
      void onCreated(res.tenantId);
    } else {
      setErr(res.kind === 'network' ? res.message : 'Could not create the fleet — try again.');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New fleet</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          We provision a tenant slot and seed a starter launcher under {email}.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Fleet / company name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 80, 'data-testid': 'new-fleet-name' }}
        />
        {err !== null && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>{err}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => void create()} disabled={busy || name.trim().length === 0} data-testid="new-fleet-create">
          {busy ? 'Creating…' : 'Create fleet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
